import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { formatTimeAgo } from '../utils/dateUtils';
import { supabase } from '../utils/supabase';

interface TrendingTrack {
  name: string;
  artist: string;
  image: string;
  rank: number;
  last_week_rank: number;
  peak_rank: number;
  weeks_on_chart: number;
  rankImprovement: number;
  normalizedImprovement: number;
  chartName: string;
  date?: string;
}

interface TrendingFeedProps {
  preview?: boolean;
  limit?: number;
  excludeCharts?: string[];
}

export function TrendingFeed({ preview = false, limit, excludeCharts = [] }: TrendingFeedProps) {
  const [trendingTracks, setTrendingTracks] = useState<TrendingTrack[]>([]);
  const [lastUpdated, setLastUpdated] = useState<string>('');

  useEffect(() => {
    const fetchTrendingData = async () => {
      try {
        // Create a map of chart sizes
        const chartSizes: { [key: string]: number } = {
          'billboard-hot-100': 100,
          'billboard-200': 200,
          'billboard-global-200': 200,
          'billboard-global-excl-us': 200,
          'billboard-artist-100': 100,
          'billboard-vinyl-albums': 25,
          'billboard-indie-store-album-sales': 25,
          'billboard-christian-songs': 50,
          'billboard-digital-song-sales': 25,
          'billboard-top-album-sales': 50,
          'billboard-hot-country-songs': 50,
          'billboard-hot-dance-electronic-songs': 25,
          'billboard-hot-latin-songs': 50,
          'billboard-emerging-artists': 50,
          'billboard-us-afrobeats-songs': 50,
          'billboard-independent-albums': 50,
          'billboard-uk-songs': 25,
          'billboard-top-streaming-albums': 50
        };

        // Fetch all charts that aren't excluded
        const { data: charts, error } = await supabase
          .from('charts')
          .select('*')
          .not('id', 'in', `(${excludeCharts.join(',')})`)
          .order('date', { ascending: false });

        if (error) {
          throw error;
        }

        // Get the most recent date
        const mostRecentDate = charts?.[0]?.date;
        if (mostRecentDate) {
          setLastUpdated(formatTimeAgo(mostRecentDate));
        }

        // Process all tracks from all charts
        const allTracks = charts.flatMap(chart => {
          const chartSize = chartSizes[chart.id] || 100;
          
          return chart.data
            .filter((track: any) => track.last_week_rank && track.last_week_rank > track.rank) // Only improved ranks
            .map((track: any) => {
              const rankImprovement = track.last_week_rank - track.rank;
              const normalizedImprovement = (rankImprovement / chartSize) * 100;

              return {
                name: track.name,
                artist: track.artist,
                image: track.image || '',
                rank: track.rank,
                last_week_rank: track.last_week_rank,
                peak_rank: track.peak_rank,
                weeks_on_chart: track.weeks_on_chart,
                rankImprovement,
                normalizedImprovement,
                chartName: chart.name,
                date: chart.date
              };
            });
        });

        // Sort by normalized improvement and limit results
        const sortedTracks = allTracks
          .sort((a, b) => {
            // First compare by normalized improvement
            const improvementDiff = b.normalizedImprovement - a.normalizedImprovement;
            if (improvementDiff !== 0) return improvementDiff;
            
            // If normalized improvements are equal, prefer higher chart positions
            return a.rank - b.rank;
          })
          .slice(0, limit || (preview ? 3 : 100));

        setTrendingTracks(sortedTracks);
      } catch (error) {
        console.error('Error fetching trending tracks:', error);
      }
    };

    fetchTrendingData();
  }, [preview, limit, excludeCharts]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-lg font-medium leading-6 text-gray-900 dark:text-white">
                Trending This Week
              </h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Biggest movers across all Billboard charts, normalized by chart size
              </p>
            </div>
            {lastUpdated && (
              <p className="text-sm text-gray-400 dark:text-gray-500">
                Last updated: {lastUpdated}
              </p>
            )}
          </div>
        </div>
        <ul className="divide-y divide-gray-200 dark:divide-gray-700">
          {trendingTracks.map((track, index) => (
            <li key={`${track.name}-${track.chartName}`} className="px-4 py-4 sm:px-6 transition-colors hover:bg-gray-50 dark:hover:bg-gray-700/50">
              <div className="flex items-center space-x-2 sm:space-x-4">
                <div className="min-w-[2rem] text-lg font-semibold text-gray-500 dark:text-gray-400">
                  {index + 1}
                </div>
                {track.image && (
                  <img 
                    src={track.image} 
                    alt="" 
                    className="h-12 w-12 object-cover rounded"
                  />
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400 truncate">
                    {track.name}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                    {track.artist}
                  </p>
                  <p className="text-xs text-gray-400 dark:text-gray-500 truncate">
                    {track.chartName}
                  </p>
                </div>
                <div className="flex flex-col items-end space-y-1">
                  <span className="text-sm text-green-500 font-medium">
                    ↑{track.rankImprovement}
                  </span>
                  <span className="text-xs text-gray-400">
                    #{track.rank}
                  </span>
                </div>
              </div>
            </li>
          ))}
        </ul>
        {preview && (
          <div className="px-4 py-4 sm:px-6 border-t border-gray-200 dark:border-gray-700">
            <Link 
              to="/trending"
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 dark:focus:ring-offset-gray-800"
            >
              View All Trending
            </Link>
          </div>
        )}
      </div>
    </div>
  );
} 